import { jsxs as _jsxs, jsx as _jsx, Fragment as _Fragment } from "react/jsx-runtime";
import * as React from 'react';
import { Component_AddOn_SearchTerms, Component_SearchMeta, Component_SearchResults } from '@nu-art/search';
import { _className, ComponentSync, LL_H_C, LL_V_L, TS_CollapsableContainerV2 } from '@nu-art/thunderstorm-frontend';
import './Component_GlobalSearch.scss';
import { ICONS } from '@app/styles';
import { PopUp_AddSearchFilter } from './PopUp_AddSearchFilter.js';
import { exists, ImplementationMissingException, numberInRange, sortArray } from '@nu-art/ts-common';
import { SearchContext_Global, UIControlledAddOnRendererMap, UIControlledAddons } from './consts.js';
export class Component_GlobalSearch extends ComponentSync {
    ref = React.createRef();
    inputRef = React.createRef();
    blurTimeout;
    // ######################### Life Cycle #########################
    __onLocationChanged = (path) => {
        if (!this.state.focused)
            return;
        //If we changed location (i.e, switched manager), we close the search
        this.setState({ focused: false }, () => this.ref.current?.blur());
    };
    deriveStateFromProps(nextProps, state) {
        state.focused ??= false;
        state.currentAddOnKeys ??= this.getInitialAddOnKeys();
        state.filtersCollapsed ??= true;
        return state;
    }
    // ######################### Logic #########################
    getInitialAddOnKeys() {
        return UIControlledAddons.filter(addOn => exists(SearchContext_Global.filter.get(addOn.key))).map(addOn => addOn.key);
    }
    onBlur = (e) => {
        const wrapper = this.ref.current;
        const input = this.inputRef.current;
        //Moving focus within this component
        if (e.relatedTarget === wrapper || wrapper.contains(e.relatedTarget)) {
            return setTimeout(() => input.focus(), 0);
        }
        //Clicked within the result right click menu
        if (e.relatedTarget?.classList.contains('search-result-context-menu-button')) {
            return setTimeout(() => input.focus(), 0);
        }
        //Return focus on to the search-results
        if (!e.relatedTarget && e.target?.classList.contains('c__search-results')) {
            return setTimeout(() => input.focus(), 0);
        }
        //Continue logic within a setTimeout to advance by a tick and let window focus update
        this.blurTimeout = setTimeout(() => {
            //Check if the window has lost focus, in which case we don't want to set "not focused"
            if (!document.hasFocus())
                return;
            this.setState({ focused: false });
        }, 100);
    };
    onFocus = () => {
        this.setState({ focused: true });
    };
    clearBlur = () => {
        clearTimeout(this.blurTimeout);
        delete this.blurTimeout;
    };
    onAddFilter = (addOnKey) => {
        this.clearBlur();
        const currentAddOnKeys = [...this.state.currentAddOnKeys, addOnKey];
        this.setState({ currentAddOnKeys, filtersCollapsed: false });
    };
    onAddOnCleared = (addOnKey) => {
        this.setState({ currentAddOnKeys: this.state.currentAddOnKeys.filter(key => key !== addOnKey) });
    };
    onAddFilterOverlay = (e) => {
        const windowElement = this.ref.current?.getElementsByClassName('component__global-search__window')?.[0];
        const rect = windowElement?.getBoundingClientRect();
        if (!rect)
            return;
        const xInRange = numberInRange(e.clientX, [rect.left, rect.right]);
        const yInRange = numberInRange(e.clientY, [rect.top, rect.bottom]);
        if (xInRange && yInRange)
            this.clearBlur();
    };
    // ######################### Render #########################
    render() {
        const className = _className('component__global-search', this.state.focused && 'focused');
        return _jsxs(LL_V_L, { className: className, onFocus: this.onFocus, onBlur: this.onBlur, innerRef: this.ref, children: [this.render_SearchTerm(), this.render_Window()] });
    }
    render_SearchTerm = () => {
        return _jsx(Component_AddOn_SearchTerms, { context: SearchContext_Global, placeholder: 'Search Anything', inputRef: this.inputRef });
    };
    render_Window = () => {
        return _jsxs(LL_V_L, { className: 'component__global-search__window', tabIndex: 1, children: [this.render_Filters(), _jsx(Component_SearchResults, { context: SearchContext_Global }), _jsx(Component_SearchMeta, { context: SearchContext_Global })] });
    };
    render_Filters = () => {
        return _jsx(TS_CollapsableContainerV2, { className: 'component__global-search__filters', headerRenderer: this.render_Filters_Header, containerRenderer: this.render_Filters_Content, collapsed: this.state.filtersCollapsed, onCollapseToggle: () => this.setState({ filtersCollapsed: !this.state.filtersCollapsed }), forceUpdate: true, animated: true });
    };
    render_Filters_Header = () => {
        return _jsxs(_Fragment, { children: ["Filters", _jsx(LL_H_C, { className: 'component__global-search__filter-actions', children: _jsx(ICONS.advisor_avn_add.component, { onClick: e => PopUp_AddSearchFilter.show(e, {
                            searchContent: SearchContext_Global,
                            onAddFilter: this.onAddFilter,
                            onOverlayClick: this.onAddFilterOverlay,
                            currentAddOnKeys: this.state.currentAddOnKeys,
                            addOnFilter: key => !!UIControlledAddons.find(addOn => addOn.key === key),
                        }) }) })] });
    };
    render_Filters_Content = () => {
        const addOns = UIControlledAddons.filter(addOn => this.state.currentAddOnKeys.includes(addOn.key));
        sortArray(addOns, addOn => this.state.currentAddOnKeys.indexOf(addOn.key));
        return _jsx(LL_V_L, { children: addOns.map(addOn => {
                const renderer = UIControlledAddOnRendererMap[addOn.key];
                if (!renderer)
                    throw new ImplementationMissingException(`Missing Addon renderer for key ${addOn.key} in map`);
                const Renderer = renderer.component;
                return _jsx(Renderer, { context: SearchContext_Global, onCleared: () => this.onAddOnCleared(addOn.key), ...renderer.props }, addOn.key);
            }) });
    };
}
